class ElProductForm extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.activeSubmitButton = null;
    this.statusResetTimeout = null;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.submitButtons = this.querySelectorAll('[type="submit"]');
    this.errorBox = this.querySelector("[data-error]");
    this.statusEl = this.querySelector(".form--status");
    this.defaultStatusText =
      this.statusEl?.textContent?.trim() || "Add To Cart";

    this.inputOptions = this.querySelectorAll('input[name="quantity_option"]');
    this.atcPrice = document.querySelectorAll("[data-atc-price]");
    this.atcCompare = document.querySelectorAll("[data-atc-compare]");
    this.atcSave = document.querySelectorAll("[data-atc-save]");
    this.subscriptionCheckbox = this.querySelector(
      'input[name="subsciption_option"]',
    );

    // get current store currency from html/data/global
    this.currencyCode =
      document.documentElement.dataset.shopCurrency ||
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.default ||
      "USD";

    if (!this.form) return;

    this.updateInclusions();

    this.submitButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.activeSubmitButton = button;
      });
    });

    this.form.addEventListener("submit", this.onSubmit);
    this.initEvents();
    this.updateOptionPrices();
    this.updateDisplayPrices();
  }

  formatMoney(value) {
    const amount = Number(value || 0);

    if (this.currencyCode === "EUR") {
      const parts = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).formatToParts(amount);

      const currency =
        parts.find((part) => part.type === "currency")?.value || "€";
      const number = parts
        .filter((part) =>
          ["integer", "group", "decimal", "fraction"].includes(part.type),
        )
        .map((part) => part.value)
        .join("");

      return `${currency}${number}`;
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: this.currencyCode,
    }).format(amount);
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.onSubmit);
    }

    if (this.inputOptions?.length) {
      this.inputOptions.forEach((input) => {
        input.removeEventListener("change", this.onOptionChange);
      });
    }

    if (this.statusResetTimeout) {
      clearTimeout(this.statusResetTimeout);
    }
  }

  get variantId() {
    return this.form?.querySelector('input[name="id"]')?.value || null;
  }

  get selectedOption() {
    return (
      this.form?.querySelector('input[name="quantity_option"]:checked') || null
    );
  }

  get selectedPrice() {
    return this.selectedOption?.dataset?.price || null;
  }

  get selectedComparePrice() {
    return this.selectedOption?.dataset?.comparePrice || null;
  }

  get selectedQuantity() {
    const paid = parseInt(this.selectedOption?.dataset?.quantity || "1", 10);
    const free = parseInt(
      this.selectedOption?.dataset?.freeQuantity || "0",
      10,
    );
    return paid + free;
  }

  get selectedPaidQuantity() {
    return parseInt(this.selectedOption?.dataset?.quantity || "1", 10);
  }

  get selectedFreeQuantity() {
    return parseInt(this.selectedOption?.dataset?.freeQuantity || "0", 10);
  }

  get selectedQuantity() {
    return this.selectedPaidQuantity + this.selectedFreeQuantity;
  }

  get subscriptionActive() {
    return this.subscriptionCheckbox?.checked || false;
  }

  get sellingPlanId() {
    return this.subscriptionCheckbox?.dataset?.sellingPlanId || null;
  }

  get subscriptionPercent() {
    return Number(
      this.subscriptionCheckbox?.dataset?.sellingPlanPercentageDiscount || 0,
    );
  }

  get subscriptionFixed() {
    return Number(
      this.subscriptionCheckbox?.dataset?.sellingPlanFixedDiscount || 0,
    );
  }

  get selectedSellingPlanOrder() {
    return Number(this.selectedOption?.dataset?.sellingPlan || 0);
  }

  get selectedSellingPlanId() {
    return this.selectedOption?.dataset?.sellingPlanId || null;
  }

  get selectedSellingPlanActive() {
    return this.selectedSellingPlanOrder > 0 && !!this.selectedSellingPlanId;
  }

  syncPrices() {
    this.updateOptionPrices();
    this.updateDisplayPrices();
  }

  updateOptionPrices() {
    if (!this.inputOptions?.length) return;

    this.inputOptions.forEach((input) => {
      const basePrice = this.getInputMoney(
        input,
        "basePriceCents",
        "basePrice",
      );

      const baseCompare = this.getInputMoney(
        input,
        "baseComparePriceCents",
        "baseComparePrice",
      );

      const hasSellingPlan =
        Number(input.dataset.sellingPlan || 0) > 0 &&
        !!input.dataset.sellingPlanId;

      let newPrice = this.applySellingPlanDiscount(basePrice, input);

      const compareForDisplay =
        hasSellingPlan && newPrice < basePrice
          ? Math.max(baseCompare, basePrice)
          : baseCompare;

      input.dataset.price = newPrice;
      input.dataset.comparePrice = compareForDisplay;

      const label = input.closest("label");
      const priceEl = label?.querySelector(".el-product-form__price");
      const compareEl = label?.querySelector(".el-product-form__compare-price");

      if (priceEl) {
        priceEl.textContent = this.formatMoney(newPrice);
      }

      if (compareEl) {
        compareEl.textContent =
          compareForDisplay > 0 ? this.formatMoney(compareForDisplay) : "";
      }
    });
  }

  parseMoneyValue(value) {
    if (value == null) return 0;

    let str = String(value).trim();

    // keep only digits, comma, dot, minus
    str = str.replace(/[^\d.,-]/g, "");

    const hasComma = str.includes(",");
    const hasDot = str.includes(".");

    if (hasComma && hasDot) {
      // 1.946,25  -> 1946.25
      // 1,946.25  -> 1946.25
      if (str.lastIndexOf(",") > str.lastIndexOf(".")) {
        str = str.replace(/\./g, "").replace(",", ".");
      } else {
        str = str.replace(/,/g, "");
      }
    } else if (hasComma) {
      // 19,46 -> 19.46
      str = str.replace(",", ".");
    }

    return parseFloat(str) || 0;
  }

  centsToMoney(cents) {
    return Number(cents || 0) / 100;
  }

  getInputMoney(input, centsKey, fallbackMoneyKey) {
    const centsValue = input.dataset?.[centsKey];

    if (centsValue !== undefined && centsValue !== "") {
      return this.centsToMoney(centsValue);
    }

    return this.parseMoneyValue(input.dataset?.[fallbackMoneyKey] || "0");
  }

  applySellingPlanDiscount(basePrice, input) {
    const sellingPlanId = input.dataset.sellingPlanId;
    const sellingPlanOrder = Number(input.dataset.sellingPlan || 0);

    if (!sellingPlanId || sellingPlanOrder <= 0) {
      return basePrice;
    }

    const discountType = input.dataset.sellingPlanDiscountType;
    const discountValue = Number(input.dataset.sellingPlanDiscountValue || 0);
    const paidQuantity = parseInt(input.dataset.quantity || "1", 10);

    let newPrice = basePrice;

    if (discountType === "percentage") {
      newPrice = basePrice * (1 - discountValue / 100);
    } else if (discountType === "fixed_amount") {
      newPrice = basePrice - this.centsToMoney(discountValue) * paidQuantity;
    } else if (discountType === "price") {
      newPrice = this.centsToMoney(discountValue) * paidQuantity;
    }

    return Math.max(newPrice, 0);
  }

  getCalculatedPrice() {
    return this.parseMoneyValue(this.selectedPrice || "0");
  }

  getCartDrawer() {
    return (
      document.querySelector("cart-drawer-component") ||
      document.querySelector("cart-drawer")
    );
  }

  openCartDrawer() {
    const cartDrawer = this.getCartDrawer();
    if (!cartDrawer) return;

    if (typeof cartDrawer.open === "function") {
      cartDrawer.open();
      return;
    }

    if ("open" in cartDrawer) {
      cartDrawer.open = true;
    }

    cartDrawer.setAttribute("open", "");
    cartDrawer.setAttribute("aria-hidden", "false");
    cartDrawer.classList.remove("is-empty");
  }

  setStatus(text, reset = false, delay = 1500) {
    if (!this.statusEl) return;

    if (this.statusResetTimeout) {
      clearTimeout(this.statusResetTimeout);
      this.statusResetTimeout = null;
    }

    this.statusEl.textContent = text;

    if (reset) {
      this.statusResetTimeout = setTimeout(() => {
        this.statusEl.textContent = this.defaultStatusText;
      }, delay);
    }
  }

  setLoadingState(isLoading) {
    if (!this.submitButtons.length) return;

    this.submitButtons.forEach((button) => {
      button.disabled = isLoading;
      button.setAttribute("aria-busy", isLoading ? "true" : "false");
    });
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.hidden = false;
    this.errorBox.textContent = message;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.hidden = true;
    this.errorBox.textContent = "";
  }

  displayPrice() {
    if (!this.atcPrice) return;
    const price = this.getCalculatedPrice();
    this.atcPrice.forEach((atcPrice) => {
      atcPrice.textContent = this.formatMoney(price);
    });
  }

  displayComparePrice() {
    if (!this.atcCompare) return;

    const comparePrice = this.parseMoneyValue(this.selectedComparePrice || "0");

    this.atcCompare.forEach((compare) => {
      compare.textContent =
        comparePrice > 0 ? this.formatMoney(comparePrice) : "";
    });
  }

  displaySavePrice() {
    if (!this.atcSave?.length) return;

    const price = this.getCalculatedPrice();
    const comparePrice = this.parseMoneyValue(this.selectedComparePrice || "0");

    const savePercent =
      comparePrice > price && comparePrice > 0
        ? Math.round(((comparePrice - price) / comparePrice) * 100)
        : 0;

    this.atcSave.forEach((saveEl) => {
      const saveText = saveEl.querySelector("[data-atc-save-text]");
      const prefix = saveEl.dataset.savePrefix || "Save";

      if (savePercent > 0) {
        saveEl.hidden = false;

        if (saveText) {
          saveText.textContent = `${prefix} ${savePercent}%`;
        }
      } else {
        saveEl.hidden = true;
        if (saveText) {
          saveText.textContent = "";
        }
      }
    });
  }

  updateDisplayPrices() {
    this.displayPrice();
    this.displayComparePrice();
    this.displaySavePrice();
  }

  updateInclusions() {
    const inclusions = document.querySelectorAll(".el-product-inclusion");
    if (!inclusions.length) return;

    const qty = this.selectedQuantity || 1;

    inclusions.forEach((el) => {
      const required = Number(el.dataset.inclusionCount || 0);
      el.classList.toggle("active", qty >= required);
    });
  }

  onOptionChange() {
    this.syncPrices();
    this.updateInclusions();
  }

  initEvents() {
    if (this.inputOptions?.length) {
      this.inputOptions.forEach((input) => {
        input.addEventListener("change", this.onOptionChange);
      });
    }

    this.subscriptionCheckbox?.addEventListener("change", () => {
      this.syncPrices();
    });
  }

  async renderCartSections() {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: {},
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!data.sections) return data;

    const cartDrawer = this.getCartDrawer();
    const currentDrawerInner =
      document.querySelector("#CartDrawer") ||
      document.querySelector(".drawer__inner");

    if (data.sections["cart-drawer"] && currentDrawerInner) {
      const drawerDoc = new DOMParser().parseFromString(
        data.sections["cart-drawer"],
        "text/html",
      );

      const newDrawerInner =
        drawerDoc.querySelector("#CartDrawer") ||
        drawerDoc.querySelector(".drawer__inner");

      if (newDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      if (cartDrawer) {
        cartDrawer.classList.remove("is-empty");
      }
    }

    if (data.sections["cart-icon-bubble"]) {
      const bubbleDoc = new DOMParser().parseFromString(
        data.sections["cart-icon-bubble"],
        "text/html",
      );

      const newBubble =
        bubbleDoc.querySelector("#cart-icon-bubble") ||
        bubbleDoc.querySelector(".shopify-section");

      const currentBubble = document.querySelector("#cart-icon-bubble");

      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }

    return data;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.clearError();

    const variantId = this.variantId;

    if (!variantId) {
      this.showError("Variant ID not found.");
      this.setStatus("Failed to Add", true);
      return;
    }

    const paidQuantity = this.selectedPaidQuantity;
    const freeQuantity = this.selectedFreeQuantity;

    const items = [];

    if (paidQuantity > 0) {
      const paidItem = {
        id: Number(variantId),
        quantity: paidQuantity,
      };

      if (this.selectedSellingPlanActive) {
        paidItem.selling_plan = Number(this.selectedSellingPlanId);
      }

      items.push(paidItem);
    }

    if (freeQuantity > 0) {
      items.push({
        id: Number(52874687021335),
        quantity: freeQuantity,
        properties: {
          _free_quantity: "true",
          _bundle_item: "true",
        },
      });
    }

    this.setLoadingState(true);
    this.setStatus("Adding...");

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => setTimeout(resolve, 20));

    try {
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      const addData = await addResponse.json();

      if (!addResponse.ok) {
        this.showError(
          addData.description ||
            addData.message ||
            "Unable to add this item to cart.",
        );
        this.setStatus("Failed to Add", true);
        return;
      }

      const cartResponse = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });

      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent("cartQuantityUpdated", {
          bubbles: true,
          detail: { cart },
        }),
      );

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: "el-product-form",
              cart,
            },
          },
        }),
      );

      await this.renderCartSections();
      this.setStatus("Added", true);
      this.openCartDrawer();
    } catch (error) {
      console.error(error);
      this.showError("Something went wrong while adding to cart.");
      this.setStatus("Failed to Add", true);
    } finally {
      this.setLoadingState(false);
      this.activeSubmitButton = null;
    }
  }
}

if (!customElements.get("el-product-form")) {
  customElements.define("el-product-form", ElProductForm);
}
